import usePropertySuiteSelection, {
  UsePropertySuiteSelectionData,
  UsePropertySuiteSelectionLoading,
} from 'src/pages/SharedPageCustomHooks/UsePropertySuiteSelection/usePropertySuiteSelection';
import { useContactFormDataFromContext } from 'src/SharedCommonContext/ContactFormContext';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
} from 'react';

export interface PropertiesDataContext {
  availableProperties: any[];
  availableSuites: any[];
  availableData: UsePropertySuiteSelectionData;
  loading: UsePropertySuiteSelectionLoading;
}

export interface PropertiesActionsContext {
  fetchHomeownerProperties: () => Promise<void>;
  fetchPropertiesSuites: (params: any) => Promise<void>;
  fetchPropertySuites: (propertyId: any) => Promise<void>;
  fetchServiceProviderManagerProperties: () => Promise<void>;
  setData: Dispatch<SetStateAction<UsePropertySuiteSelectionData>>;
  updateDisplayedSuitesInUIUponRemovingProperty: (
    selectedPropertyToRemove: any
  ) => void;
  updateDisplayedSuitesInUIUponAddingProperty: (
    selectedPropertyToAdd: any
  ) => Promise<void>;
}

export function createPropertiesContextProvider() {
  const DataContext = createContext<any | undefined>(undefined);
  const ActionsContext = createContext<any | undefined>(undefined);

  interface PropertiesProviderProps {
    children: ReactNode;
  }

  const PropertiesForContactsProvider = ({
    children,
  }: PropertiesProviderProps) => {
    const { contactFormData } = useContactFormDataFromContext();

    const propertiesHook = usePropertySuiteSelection({
      contactFormData,
      initialValue: {},
    });

    const dataValue: PropertiesDataContext = {
      availableProperties: propertiesHook.data.availableProperties || [],
      availableSuites: propertiesHook.data.availableSuites || [],
      availableData: propertiesHook.data,
      loading: propertiesHook.loading,
    };

    const actionsValue: PropertiesActionsContext = {
      fetchHomeownerProperties: propertiesHook.fetchHomeownerProperties,
      fetchPropertiesSuites: propertiesHook.fetchPropertiesSuites,
      fetchPropertySuites: propertiesHook.fetchPropertySuites,
      fetchServiceProviderManagerProperties:
        propertiesHook.fetchServiceProviderManagerProperties,
      setData: propertiesHook.setData,
      updateDisplayedSuitesInUIUponRemovingProperty:
        propertiesHook.updateDisplayedSuitesInUIUponRemovingProperty,
      updateDisplayedSuitesInUIUponAddingProperty:
        propertiesHook.updateDisplayedSuitesInUIUponAddingProperty,
    };

    return (
      <ActionsContext.Provider value={actionsValue}>
        <DataContext.Provider value={dataValue}>
          {children}
        </DataContext.Provider>
      </ActionsContext.Provider>
    );
  };

  const usePropertiesDataFromContext = () => {
    const context = useContext(DataContext);
    if (context === undefined) {
      throw new Error(
        'usePropertiesDataFromContext must be used within a PropertiesProvider'
      );
    }
    return context;
  };

  const usePropertiesActionsFromContext = () => {
    const context = useContext(ActionsContext);
    if (context === undefined) {
      throw new Error(
        'usePropertiesActionsFromContext must be used within a PropertiesProvider'
      );
    }
    return context;
  };

  return {
    PropertiesForContactsProvider,
    usePropertiesDataFromContext,
    usePropertiesActionsFromContext,
  };
}

export const {
  PropertiesForContactsProvider,
  usePropertiesDataFromContext,
  usePropertiesActionsFromContext,
} = createPropertiesContextProvider();
